import * as DialogPrimitive from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import * as styles from "./Modal.module.scss";
import { useContext, useState, useRef, type PropsWithChildren } from "react";
import { AnimatePresence, motion } from "motion/react";
import {
  Header as HeaderLayout,
  Heading,
  IconAction,
  type IconActionProps,
} from "../_shared/Header/Header";
import * as headerStyles from "../_shared/Header/Header.module.scss";
import { ScrollArea } from "../ScrollArea/ScrollArea";
import { Close as CloseIcon } from "../Icon/Close";
import { ModalContext } from "./ModalContext";
import { cx } from "class-variance-authority";
import { fadeTransition, surfaceTransition } from "../_shared/transitions";
import { useDragToDismiss } from "../hooks/useDragToDismiss";
import { DESKTOP_MEDIA_QUERY, useMediaQuery } from "../hooks/useMediaQuery";

export interface ModalRootProps extends PropsWithChildren {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * When false, the header close button, drag, outside taps and Escape key are
   * all disabled, forcing the user to interact with content instead. The close
   * button and grab bar stay mounted so the modal's height can't shift.
   * Defaults to true.
   */
  dismissible?: boolean;
}

/** Circular close button for a header's end slot; renders a CloseIcon unless
 * given children */
const CloseAction = ({ children, className, ...props }: IconActionProps) => {
  return (
    <DialogPrimitive.Close asChild>
      <IconAction
        aria-label="Close"
        className={cx(headerStyles.closeIcon, className)}
        {...props}
      >
        {children ?? <CloseIcon />}
      </IconAction>
    </DialogPrimitive.Close>
  );
};

const Header = ({ children }: PropsWithChildren) => {
  const { dismissible } = useContext(ModalContext);

  return (
    <HeaderLayout>
      {children}
      <CloseAction disabled={!dismissible} />
    </HeaderLayout>
  );
};

/** visuallyHidden keeps the dialog's accessible name/description when the
 * visible heading is rendered elsewhere (e.g. per-step Wizard.Header) */
interface ModalTitleProps extends PropsWithChildren {
  visuallyHidden?: boolean;
}

const Title = ({ children, visuallyHidden }: ModalTitleProps) => {
  const title = (
    <DialogPrimitive.Title
      className={visuallyHidden ? undefined : headerStyles.title}
    >
      {children}
    </DialogPrimitive.Title>
  );

  return visuallyHidden ? (
    <VisuallyHidden asChild>{title}</VisuallyHidden>
  ) : (
    title
  );
};

const Subtitle = ({ children, visuallyHidden }: ModalTitleProps) => {
  const subtitle = (
    <DialogPrimitive.Description
      className={visuallyHidden ? undefined : headerStyles.subtitle}
    >
      {children}
    </DialogPrimitive.Description>
  );

  return visuallyHidden ? (
    <VisuallyHidden asChild>{subtitle}</VisuallyHidden>
  ) : (
    subtitle
  );
};

const Footer = ({ children }: PropsWithChildren) => {
  return <div className={styles.footer}>{children}</div>;
};

const Content = ({ children }: PropsWithChildren) => {
  return <div className={styles.content}>{children}</div>;
};

const sheetMotion = {
  initial: { y: "100%" },
  animate: { y: 0 },
  exit: { y: "100%" },
  transition: surfaceTransition,
} as const;

const dialogMotion = {
  initial: { opacity: 0, scale: 0.96, y: "2%" },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.96, y: "2%" },
  transition: fadeTransition,
} as const;

const Body = ({
  children,
  className,
  onOpened,
}: PropsWithChildren<{
  className?: string;
  /** Called once the open animation finishes. */
  onOpened?: () => void;
}>) => {
  const { open, setOpen, dismissible } = useContext(ModalContext);
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY);

  const canDrag = !isDesktop && dismissible;
  const isAnimatingRef = useRef(false);
  const blockWhileAnimating = (event: Event) => {
    if (!dismissible || isAnimatingRef.current) {
      event.preventDefault();
      return;
    }
  };
  const blockDismiss = dismissible
    ? undefined
    : (event: Event) => event.preventDefault();

  const { dragControls, dragProps } = useDragToDismiss({
    edge: "bottom",
    enabled: canDrag,
    onDismiss: () => setOpen(false),
  });

  return (
    <DialogPrimitive.Portal forceMount>
      <AnimatePresence>
        {open && (
          <>
            <DialogPrimitive.Overlay forceMount asChild>
              <motion.div
                className={styles.overlay}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={fadeTransition}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content
              forceMount
              asChild
              onEscapeKeyDown={blockDismiss}
              onPointerDownOutside={blockWhileAnimating}
              onInteractOutside={blockWhileAnimating}
            >
              <motion.div
                className={cx(styles.body, className)}
                {...dragProps}
                dragListener={false} // grab bar at the top of the sheet triggers the drag, not the container
                {...(isDesktop ? dialogMotion : sheetMotion)}
                onAnimationStart={() => {
                  isAnimatingRef.current = true;
                }}
                onAnimationComplete={(definition) => {
                  isAnimatingRef.current = false;
                  // Both open animations end at y: 0; the exits end off-screen.
                  if (
                    typeof definition === "object" &&
                    "y" in definition &&
                    definition.y === 0
                  ) {
                    onOpened?.();
                  }
                }}
              >
                <ScrollArea.Root
                  orientation="vertical"
                  className={styles.inner}
                >
                  <ScrollArea.Viewport className={styles.innerViewport}>
                    <div
                      className={cx(styles.grabBar, {
                        [styles.grabBarInert]: !dismissible,
                      })}
                      draggable={false}
                      onPointerDown={
                        canDrag
                          ? (event) => {
                              event.preventDefault();
                              dragControls.start(event);
                            }
                          : undefined
                      }
                    />
                    {children}
                  </ScrollArea.Viewport>
                  <ScrollArea.Scrollbar />
                </ScrollArea.Root>
              </motion.div>
            </DialogPrimitive.Content>
          </>
        )}
      </AnimatePresence>
    </DialogPrimitive.Portal>
  );
};

const Close = (props: DialogPrimitive.DialogCloseProps) => {
  return <DialogPrimitive.Close asChild {...props} />;
};

const Trigger = (props: DialogPrimitive.DialogTriggerProps) => {
  return <DialogPrimitive.Trigger asChild {...props} />;
};

/**
 *  Renders as a bottom sheet on mobile and a centered dialog from tablet up.
 *  Can be controlled or uncontrolled.
 */
const Root = ({
  children,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  dismissible = true,
}: ModalRootProps) => {
  const [localOpen, setLocalOpen] = useState(defaultOpen);
  const open = controlledOpen ?? localOpen;

  const setOpen = (next: boolean) => {
    if (controlledOpen === undefined) {
      setLocalOpen(next);
    }
    onOpenChange?.(next);
  };

  return (
    <ModalContext value={{ open, setOpen, dismissible }}>
      <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
        {children}
      </DialogPrimitive.Root>
    </ModalContext>
  );
};

export const Modal = {
  Root,
  Trigger,
  Body,
  Content,
  Header,
  Heading,
  Title,
  Subtitle,
  Footer,
  Close,
  CloseAction,
  IconAction,
};
